import { ChevronDown } from "lucide-react";
/**
 * ImportOwnerMenu — the "Imported by:" scope dropdown (R-F2 / R-F3).
 *
 * Wire mapping (canonical): the menu's presentation labels map to the `owner`
 * query value — `Me` → `you`, `Everyone` → `all`, a recruiter row → that
 * recruiter's EMAIL. The FE never sends a display name.
 *
 * The label and the value diverge on purpose. "Imported by: You" read as a
 * question about the reader rather than a scope, so the copy became `Me`
 * (2026-08-14); the value stayed `you` because `router.py` validates
 * `you|all|<email>` — renaming it would 422 every request this control makes.
 * `ImportOwnerMenu.test.tsx` pins both halves.
 *
 * The per-recruiter list arrives via the `owners` prop, sourced from
 * `useImportOwners` (`GET /imports/owners`). It is deliberately NOT derived
 * from the rows the caller is currently showing: doing that made the options a
 * function of the tag chip, the search text, the page size and the owner scope
 * itself, so picking a recruiter left them as the only option in the menu.
 */
import { useEffect, useId, useRef, useState } from "react";

export interface OwnerOption {
  /** The owner email (the wire value for a recruiter row). */
  email: string;
  /** Resolved display name; falls back to the email when null. */
  name: string | null;
}

export interface ImportOwnerMenuProps {
  /** Current wire value: `you` | `all` | `<email>`. */
  value: string;
  /** Caller's own email — used to resolve the `Me` label + filter the list. */
  meEmail: string;
  /**
   * Every import owner firm-wide (see `useImportOwners`), or `null` while the
   * request is still in flight. A FAILED request passes `[]`, not `null` — the
   * caller's error banner carries that meaning, and a "Loading recruiters…" row
   * beside "Couldn't load the recruiter list" would contradict it.
   *
   * `null` rather than `[]`, following `SearchesToolbar`'s `total`: an empty
   * array is a factual claim that nobody else has imports, and rendering it
   * while the request is in flight makes the pending menu byte-identical to
   * the failed one — the "no colleague has imports" misreading this control
   * stopped deriving its options from the visible page to avoid. The type
   * makes the loading state impossible to forget and `loading && owners.length`
   * impossible to represent.
   */
  owners: readonly OwnerOption[] | null;
  onChange: (owner: string) => void;
  /** The trigger's accessible name. Company Lists reuses this menu and names
   *  its own noun; People Lists keeps the default. */
  ariaLabel?: string;
}

function displayFor(opt: OwnerOption): string {
  return opt.name?.trim() ? opt.name : opt.email;
}

export function ImportOwnerMenu({
  value,
  meEmail,
  owners,
  onChange,
  ariaLabel = "Change people list scope",
}: ImportOwnerMenuProps) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Dismissal on outside click + Escape. This menu shipped without either, so
  // the only way to close it was to pick a scope or re-click the trigger — a
  // reader who opened it to look and then clicked the table was left with a
  // panel covering the rows. Same pattern (and same reasoning) as
  // `SearchReportExportMenu`: bound only while open, capture phase, and the
  // wrapper check keeps it out of the trigger's and the items' way.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Escape must not strand focus on a node that is about to unmount.
      triggerRef.current?.focus();
    }

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  const loading = owners === null;

  // Per-recruiter list: distinct owners excluding the caller, sorted by label
  // (locale-aware — the endpoint is deliberately unordered).
  const others = (owners ?? [])
    .filter((o) => o.email.toLowerCase() !== meEmail.toLowerCase())
    .sort((a, b) => displayFor(a).localeCompare(displayFor(b)));

  // The button label for the current scope.
  let currentLabel = "Me";
  if (value === "all") currentLabel = "Everyone";
  else if (value !== "you") {
    const match = (owners ?? []).find((o) => o.email === value);
    currentLabel = match ? displayFor(match) : value;
  }

  // "Me" renders in accent when it is the active scope (prototype colour rule).
  const isYou = value === "you";

  function choose(owner: string) {
    onChange(owner);
    setOpen(false);
  }

  return (
    <div className="relative" data-testid="import-owner-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={ariaLabel}
        data-testid="import-owner-trigger"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-label text-text hover:border-border-hover"
      >
        <span className="text-text-secondary">Imported by:</span>
        <span className={isYou ? "text-accent font-semibold" : "text-text"}>{currentLabel}</span>
        <ChevronDown className="h-4 w-4 text-text-secondary" aria-hidden="true" />
      </button>

      {open ? (
        // role="menu" with direct role="menuitem" buttons — no <li role="none">
        // wrappers (which biome's a11y rules flag); the menu items are the
        // focusable buttons themselves.
        <div
          id={menuId}
          role="menu"
          // `max-h` + scroll because this list is no longer page-bounded.
          // It used to hold at most one page of owners (<=8 in the picker, <=5
          // on the Imports page); sourcing it from `/imports/owners` makes it
          // one row per recruiter in the firm, so at ~40 it would run off the
          // viewport with no way back.
          //
          // Scrolling is the floor, not the finish: `SearchesToolbar.tsx:11-15`
          // chose a NATIVE `<select>` for the identical control over the
          // identical data, precisely because a native list also brings
          // type-ahead and the platform's long-list handling, "none of which
          // the menu has". Converting this one is the right end state and a
          // wider change than the bug that exposed it.
          className="absolute right-0 z-10 mt-1 max-h-72 min-w-48 overflow-y-auto rounded-md border border-border bg-surface py-1 shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => choose("you")}
            className="block w-full px-3 py-1.5 text-left text-label text-accent font-semibold hover:bg-accent-muted"
          >
            Me
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => choose("all")}
            className="block w-full px-3 py-1.5 text-left text-label text-text hover:bg-accent-muted"
          >
            Everyone
          </button>
          {loading || others.length > 0 ? (
            <hr className="my-1 border-0 border-t border-border" />
          ) : null}
          {loading ? (
            // A disabled row rather than a spinner or an empty gap: the three
            // states (loading / nobody else / failed) must be distinguishable,
            // and You + Everyone stay usable throughout.
            <button
              type="button"
              role="menuitem"
              disabled
              data-testid="import-owner-loading"
              className="block w-full px-3 py-1.5 text-left text-label text-text-muted"
            >
              Loading recruiters…
            </button>
          ) : null}
          {others.map((o) => (
            <button
              key={o.email}
              type="button"
              role="menuitem"
              onClick={() => choose(o.email)}
              className="block w-full px-3 py-1.5 text-left text-label text-text hover:bg-accent-muted"
            >
              {displayFor(o)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
